import BaseModel from '../base';

import constants, { DEFAULT_CARD_NUMBER_TEMPLATE } from './constants';
import glossary, { REQUEST_STATUS_DATA as STATUS_DATA } from './glossary';
import { REQUEST_STATUS_DATA } from './user/glossary';
import { rHandler } from '@/plugins/RHandler';
import { DEFAULT_PARAMS, objectsRHandler } from '@/utils/objectsRHandler';
import { apiService } from '@/services/APIService';

import { rServices } from '@/plugins/api';
import OrganizerProfile from '../organizer-profile';
import AssociationClub from './club';
import AssociationFeature from './feature';
import AssociationOffice from './office';
import { getInviteLink } from '@/modules/UIDesign/Invites/functions';
import { INVITE_TYPE } from '@/modules/UIDesign/Invites/constants';
import AssociationUserRestriction from './user-restrictions';
import { EVENT_OPTIONS_AGES } from '@/modules/Events/constants';
import AssociationCardNumberPool from './card-number-pool';

export default class EventAssociation extends BaseModel {
  constructor(association, url) {
    super(association, url);
    this.image = association.image || null;
    this.description = association.description;
    this.my_request = association.my_request || null;
    this.my_request_status = association.my_request_status ?? this.my_request?.status ?? null;
    this.my_request_representative_office_id = association.my_request_representative_office_id
      ?? this.my_request?.representative_office_id
      ?? null;
    this.is_user_moderation = association.is_user_moderation;
    this.is_available_for_minors = association.is_available_for_minors;
    this.legal_member_full_name = association.legal_member_full_name;
    this.active_users_count = association.active_users_count ?? 0;
    this.active_clubs_count = association.active_clubs_count ?? 0;
    this.events_count = association.events_count ?? 0;
    this.active_accreditation_organizers_count = association.active_accreditation_organizers_count ?? 0;
    this.representative_offices_count = association.representative_offices_count ?? 0;
    this.status = association.status;

    this.min_age = association.min_age || EVENT_OPTIONS_AGES.min;
    this.max_age = association.max_age || EVENT_OPTIONS_AGES.max;

    // Не путать "organizers" (управляющие) и "accreditation-organizers" (аккредитованные организаторы)
    this.organizers = (association.organizers || []).map((i) => new OrganizerProfile(i));
    this.clubs = (association.clubs || []).map((i) => new AssociationClub(i));
    this.users = association.users || [];
    this.owner = association.owner || null;

    this.miniature = association.miniature || null;

    this.my = association.my || false; // expand=my ; По сути, просто смотрит, участник ли я (user_id)

    this.features = (association.features || []).map((i) => new AssociationFeature(i));

    this.card_number_template = DEFAULT_CARD_NUMBER_TEMPLATE;
  }

  get organizer() {
    const [main] = this.organizers;
    return main;
  }

  get requestStatusData() {
    const status = this.my_request?.status ?? this.my_request_status;
    return status ? REQUEST_STATUS_DATA?.[status] : undefined;
  }

  get statusData() {
    return this.status ? STATUS_DATA?.[this.status] : undefined;
  }

  static get constants() {
    return constants;
  }

  static get glossary() {
    return glossary;
  }

  get cardNumbers() {
    const { card_number_template: template } = this;
    const digits = (template.match(/#/g) || []).length;
    return {
      digits,
      template,
      formattedToNumber: (someString = '') => Number(String(someString).replace(/\D/g, '')),
      numberToFormatted: (someNumber = 0) => String(someNumber).padStart(digits, '0'),
      numberToTemplate: (someNumber = 0) => {
        const raw = this.cardNumbers.numberToFormatted(someNumber);
        const digitIterator = raw[Symbol.iterator]();
        return template.replace(/#/g, () => digitIterator.next().value);
      },
    };
  }

  get api() {
    const { url, id } = this;
    return {
      get features() {
        const path = `${url}/${id}/features`;
        return {
          ...objectsRHandler(path, AssociationFeature),
        };
      },
      get offices() {
        // Representative offices are exposed only from the private association API.
        const path = `event-associations/${id}/representative-offices`;
        return {
          ...objectsRHandler(path, AssociationOffice),
          invite({ args, params, obs } = DEFAULT_PARAMS) {
            const [officeId] = args;
            return rHandler(
              apiService.get(`${path}/${officeId}/invite`, params),
              (response) => ({
                response,
                item: getInviteLink(response.data.data.invite, INVITE_TYPE.associationOffice),
              }),
              (error) => ({ error }),
              obs,
            );
          },
        };
      },
      get ratingOptions() {
        const path = `${url}/${id}/rating-options`;

        return {
          show({ params } = DEFAULT_PARAMS) {
            return rHandler(
              apiService.get(path, params),
              (response) => ({
                response,
                item: response.data.data || {},
              }),
              (error) => ({ error }),
            );
          },
          save({ data, params } = DEFAULT_PARAMS) {
            return rHandler(
              apiService.post(path, data, params ? { params } : undefined),
              (response) => ({
                response,
                item: response.data.data || {},
              }),
              (error) => ({ error }),
            );
          },
        };
      },
      get userRestrictions() {
        const path = `${url}/${id}/user-restrictions`;
        return {
          ...objectsRHandler(path, AssociationUserRestriction),
        };
      },
      get cardNumbers() {
        const path = `${url}/${id}`;
        return {
          ...objectsRHandler(`${path}/card-number-pools`, AssociationCardNumberPool),
          generate({ params, obs } = DEFAULT_PARAMS) {
            return rHandler(
              apiService.post(`${path}/generate-card-number`, params),
              (response) => ({
                response,
                item: response.data.data?.card_number,
              }),
              (error) => ({ error }),
              obs,
            );
          },
        };
      },
    };
  }
}
